import type { ReactElement } from 'react'
import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { MoonIcon, SunIcon } from './modesToggleIcons'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { useMetroMapStore } from '../state/useMetroMapStore'
import { useNightModeActions } from '../state/nightModeActions'
import {
  reportNightModeDisabled,
  reportNightModeEnabled,
  reportNightModeToggleAttempted,
} from '../analytics/nightModeEvents'
import styles from './ModesToggle.module.css'

// TODO(night-tube): premium-gate-deferred — web paywall policy at
// project_metro_web_no_paywall. iOS / Android gate `toggleNightMode()` behind
// an Adapty paywall placement (`com.mmthstudio.night-tube.v1`); web ships
// free for all users until the web Adapty integration lands. Do NOT add a
// paywall here without an explicit lead sign-off.
//
// Bridge note (sizing): shape is the 52×96 vertical slider capsule, mirroring
// iOS `MetroMapMetalRootView+RouteDetails.swift` `ModesToggle` and Android
// `ui/ModesToggle.kt` — sun pinned top, moon pinned bottom, a coloured knob
// (52×52 circle) that springs vertically between them. This REVERSES the
// 2026-06-01 "52×52 round button" PM decision (Night Tube Notion task
// 3721b27c8ca081acb24df4dcd53f79d7): updated PM decision 2026-07-06 — web must
// match iOS/Android so the control reads as a slider, not a plain button.
// Knob colours are the iOS/Android cross-platform canon (sun #FCCA1E, night
// knob #2357B0). Icons remain Phosphor `Sun`/`Moon` as a PLACEHOLDER — a
// unified sun/moon asset (to be supplied) will replace them across all three
// platforms in a follow-up; do not treat the current glyphs as final.

export interface ModesToggleProps {
  /**
   * Analytics sink. Optional — defaults to {@link realMetroAnalyticsReporter}
   * so the night-mode events forward to GA4 + Amplitude in
   * production (no-op outside production / before consent). Tests inject a
   * spy.
   */
  readonly reporter?: MetroAnalyticsReporter
}

export function ModesToggle(props: ModesToggleProps): ReactElement {
  const { t } = useTranslation('common')
  const store = useMetroMapStore()
  const currentMode = useSyncExternalStore(store.subscribe, () => store.getState().currentMode)
  // TASK-412 F (mirror iOS @54eb691ef): hide the day/night toggle while a
  // route is building / highlighted. Flipping night mode re-filters the
  // map (filterMapDataByMode) and would break the in-progress / highlighted
  // route render, so the control is hidden — and made non-interactive —
  // for the {loading, results} statuses, and shown for {collapsed, empty,
  // error}. Same predicate on mobile + desktop (one fixed-position toggle).
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)
  const isHidden = routeStatus.kind === 'loading' || routeStatus.kind === 'results'
  const { toggleNightMode } = useNightModeActions()
  const reporter = props.reporter ?? realMetroAnalyticsReporter

  const isNight = currentMode === 'night'

  const onClick = (): void => {
    // Read live from the store (not the captured `currentMode` snapshot) so
    // tests / programmatic dispatches that mutate state between renders are
    // reflected in the analytics payload accurately.
    const currentIsNight = store.getState().currentMode === 'night'
    reportNightModeToggleAttempted(reporter, {
      currentIsNight,
      targetIsNight: !currentIsNight,
    })
    const { next } = toggleNightMode()
    if (next === 'night') {
      // Web has no paywall gate (decision #4) — the only enable-path is a
      // direct web-user toggle. Cross-platform `source` contract documented
      // on [NightModeSource]. iOS / Android emit `premium_user` or
      // `paywall`; web always emits `web_user`.
      reportNightModeEnabled(reporter, { source: 'web_user' })
    } else {
      reportNightModeDisabled(reporter)
    }
  }

  return (
    <button
      type="button"
      className={isHidden ? `${styles.toggle} ${styles.hidden}` : styles.toggle}
      onClick={onClick}
      aria-label={t('ui.modes.toggle_aria', { defaultValue: 'Toggle night mode' })}
      aria-pressed={isNight}
      // F: while hidden the control is non-interactive — pointer-events
      // are off via `.hidden` (CSS), and we drop it from the a11y tree +
      // tab order so a screen-reader / keyboard user can't reach a
      // control that is fading away mid route-build.
      aria-hidden={isHidden || undefined}
      tabIndex={isHidden ? -1 : undefined}
      data-hidden={isHidden || undefined}
      data-testid="modes-toggle"
      data-mode={currentMode}
    >
      {/* Fixed background glyphs — sun pinned top (white), moon pinned bottom
          (black), always both visible; the knob slides over the active one.
          Mirrors iOS/Android. Placeholder Phosphor icons pending the unified
          asset swap. */}
      <span className={styles.glyphs} aria-hidden="true">
        <SunIcon className={styles.glyphSun} size={24} />
        <MoonIcon className={styles.glyphMoon} size={24} />
      </span>
      {/* Sliding knob — coloured circle carrying the active glyph in white
          (knob sets `color:#fff`, glyphs use currentColor); springs y 0 → 48px
          on night. */}
      <span className={styles.knob} data-night={isNight || undefined} aria-hidden="true">
        {isNight ? <MoonIcon size={24} /> : <SunIcon size={24} />}
      </span>
    </button>
  )
}
